import PennyInput from "@components/Inputs/PennyInput"
import { zodResolver } from "@hookform/resolvers/zod"
import type React from "react"
import { useState, useEffect } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { twMerge } from "tailwind-merge"
import { z } from "zod"
import { ReactComponent as PennyLogo } from "../../assets/icons/penny_logo.svg"
import { Link } from "react-router-dom"

type ResetPasswordProps = {
  onSubmit: (password: string) => void
  email: string
}

const resetPasswordSchema = z
  .object({
    password: z
      .string({ required_error: "Required" })
      .min(8, { message: "Password must be at least 8 characters" })
      .regex(/[A-Z]/, { message: "Password must contain at least one uppercase letter" })
      .regex(/[a-z]/, { message: "Password must contain at least one lowercase letter" })
      .regex(/[0-9]/, { message: "Password must contain at least one number" }),
    confirmPassword: z.string({ required_error: "Required" }).min(1, { message: "Required" }),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: "custom",
        message: "Passwords do not match",
        path: ["passwordMatch"],
      })
    }
  })
type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>

interface FormValues {
  password: string
  confirmPassword: string
  passwordMatch: string
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ onSubmit, email }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [resetError, setResetError] = useState<string | null>(null)

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(resetPasswordSchema),
  })

  // Watch password field to calculate strength
  const password = watch("password", "")

  // Calculate password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0)
      return
    }

    let strength = 0

    // Length check
    if (password.length >= 8) strength += 1

    // Uppercase check
    if (/[A-Z]/.test(password)) strength += 1

    // Lowercase check
    if (/[a-z]/.test(password)) strength += 1

    // Number check
    if (/[0-9]/.test(password)) strength += 1

    // Special character check
    if (/[^A-Za-z0-9]/.test(password)) strength += 1

    setPasswordStrength(strength)
  }, [password])

  const onFormSubmit: SubmitHandler<ResetPasswordSchema> = (data) => {
    setIsSubmitting(true)
    setResetError(null)

    try {
      const { password } = data
      onSubmit(password)

      // If onSubmit doesn't throw or redirect, we'll add a timeout to reset the submitting state
      const timeout = setTimeout(() => {
        setIsSubmitting(false)
      }, 5000)

      return () => clearTimeout(timeout)
    } catch (error) {
      console.error("Error during password reset:", error)
      setResetError(error instanceof Error ? error.message : "Failed to reset password. Please try again.")
      setIsSubmitting(false)
    }
  }

  // Get strength indicator color
  const getStrengthColor = () => {
    if (passwordStrength <= 1) return "bg-pn_red-500"
    if (passwordStrength <= 3) return "bg-pn_orange-500"
    return "bg-pn_green-500"
  }

  // Get strength text
  const getStrengthText = () => {
    if (passwordStrength <= 1) return "Weak"
    if (passwordStrength <= 3) return "Medium"
    return "Strong"
  }

  return (
    <section className="w-full grow flex items-center justify-center">
      <div className="max-w-full md:max-w-[400px] md:my-4 w-full py-10 mx-auto md:border md:border-pn_gray-200 bg-pn_paper-1 rounded-3xl">
        <div className="w-full py-4 px-6 flex items-center justify-center">
          <PennyLogo width={81} height={32} />
        </div>

        <div className="flex flex-col gap-y-4 py-4 px-6">
          <h2 className="text-center text-pn-large-title text-pn_gray-950">Reset Password</h2>
          <span className="text-center text-pn-body-regular !font-normal text-pn_gray-600">
            Please enter a new password for: <span className="text-pn-body-emphasis">{email}</span>
          </span>
        </div>

        {resetError && (
          <div className="px-6 mb-2">
            <div className="bg-pn_red-50 border border-pn_red-500 text-pn_red-500 p-3 rounded-lg text-pn-callout-emphasis text-center">
              {resetError}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-y-4 py-4 px-6">
          <div className={twMerge(errors.password?.message?.length ? "flex flex-col gap-y-2" : "block")}>
            <PennyInput
              {...register("password")}
              type="password"
              label="Password"
              id="password"
              name="password"
              placeholder="Password"
              error={errors.password?.message?.length}
              withToggleShowPassword
              disabled={isSubmitting}
            />
            {!!errors.password?.message && (
              <span className="text-pn-callout-emphasis text-pn_red-500">{errors.password.message}</span>
            )}

            {/* Password strength indicator */}
            {password && (
              <div className="mt-2">
                <div className="flex justify-between mb-1">
                  <span className="text-pn-caption-regular text-pn_gray-600">Password strength:</span>
                  <span
                    className={`text-pn-caption-regular ${passwordStrength <= 1
                      ? "text-pn_red-500"
                      : passwordStrength <= 3
                        ? "text-pn_orange-500"
                        : "text-pn_green-500"
                      }`}
                  >
                    {getStrengthText()}
                  </span>
                </div>
                <div className="w-full h-1 bg-pn_gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getStrengthColor()}`}
                    style={{ width: `${(passwordStrength / 5) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div className={twMerge(errors.confirmPassword?.message?.length ? "flex flex-col gap-y-2" : "block")}>
            <PennyInput
              {...register("confirmPassword")}
              name="confirmPassword"
              type="password"
              id="confirmPassword"
              label="Confirm Password"
              placeholder="Confirm Password"
              error={errors.confirmPassword?.message?.length}
              withToggleShowPassword
              disabled={isSubmitting}
            />
            {(!!errors.confirmPassword?.message || !!errors.passwordMatch?.message) && (
              <span className="text-pn-callout-emphasis text-pn_red-500">
                {errors?.confirmPassword?.message || errors?.passwordMatch?.message}
              </span>
            )}
          </div>

          <button type="submit" className="btn-primary-lg relative" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="opacity-0">Set New Password</span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              </>
            ) : (
              "Set New Password"
            )}
          </button>
        </form>

        <div className="py-4 px-6 flex items-center justify-center">
          <span className="text-pn-callout-regular text-center">
            By proceeding, you agree to the{" "}
            <Link to="/" className="text-pn-callout-link">
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link to="/" className="text-pn-callout-link">
              Privacy Policy
            </Link>
          </span>
        </div>
      </div>
    </section>
  )
}
export default ResetPassword
